'use client';

import { useEffect, useRef, useState } from 'react';

interface UseIntersectionOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useIntersection<T extends HTMLElement = HTMLDivElement>({
  threshold  = 0.1,
  rootMargin = '0px',
  once       = true,
}: UseIntersectionOptions = {}) {
  const ref     = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, visible };
}

/** Convenience: returns Tailwind classes for a fade-up reveal */
export function revealClasses(visible: boolean, delay = 0): string {
  const delayClass = delay ? `delay-${delay}` : '';
  return [
    'transition-all duration-500',
    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5',
    delayClass,
  ]
    .filter(Boolean)
    .join(' ');
}

export default useIntersection;
